import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building, Save, Loader2, Wallet, Calendar, Wrench, Briefcase, Banknote, Car, Home, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const fmt = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Champs structurés par bloc
const ANNUEL_FIELDS = [
  { key: 'comptable_annuel', label: 'Expert-comptable (forfait annuel)', placeholder: '2 400' },
  { key: 'rc_pro_annuel', label: 'Responsabilité Civile Pro', placeholder: '800' },
  { key: 'decennale_annuelle', label: 'Assurance décennale', placeholder: '2 500' },
  { key: 'garantie_financiere_annuelle', label: 'Garantie financière MdB', placeholder: '2 000' },
  { key: 'cfe_annuel', label: 'CFE', placeholder: '500' },
  { key: 'cci_cma_annuel', label: 'CCI / CMA / cotisation pro', placeholder: '300' },
  { key: 'juridique_annuel', label: 'Frais juridiques (AG, dépôts)', placeholder: '500' },
  { key: 'formation_annuelle', label: 'Formation continue', placeholder: '1 000' },
  { key: 'site_web_annuel', label: 'Site web / hébergement', placeholder: '600' },
  { key: 'autres_annuel', label: 'Autres frais annuels', placeholder: '0' },
];

const MENSUEL_FIELDS = [
  { key: 'comptable_mensuel', label: 'Comptable (si mensualisé)', placeholder: '0' },
  { key: 'banque_mensuel', label: 'Compte bancaire pro', placeholder: '25' },
  { key: 'internet_telephone_mensuel', label: 'Internet / Téléphone', placeholder: '60' },
  { key: 'logiciels_mensuel', label: 'Logiciels / SaaS', placeholder: '100' },
  { key: 'loyer_mensuel', label: 'Loyer bureau / domiciliation', placeholder: '0' },
  { key: 'charges_locaux_mensuel', label: 'Charges locaux (eau/élec)', placeholder: '0' },
  { key: 'marketing_mensuel', label: 'Marketing / Pub', placeholder: '0' },
  { key: 'autres_mensuel', label: 'Autres frais mensuels', placeholder: '0' },
];

const VEHICULE_FIELDS = [
  { key: 'vehicule_mensuel', label: 'Crédit / Leasing véhicule', placeholder: '450' },
  { key: 'assurance_auto_mensuelle', label: 'Assurance auto pro', placeholder: '70' },
  { key: 'carburant_mensuel', label: 'Carburant', placeholder: '200' },
  { key: 'entretien_vehicule_mensuel', label: 'Entretien (forfait/mois)', placeholder: '60' },
];

const REMUNERATION_FIELDS = [
  { key: 'remuneration_brute_mensuelle', label: 'Rémunération brute mensuelle', placeholder: '2 500' },
  { key: 'charges_remuneration_pct', label: '% charges sociales (TNS ~45%, salarié ~80%)', placeholder: '45', suffix: '%' },
  { key: 'prevoyance_mensuelle', label: 'Prévoyance', placeholder: '120' },
  { key: 'mutuelle_mensuelle', label: 'Mutuelle', placeholder: '80' },
];

const FieldGroup = ({ title, icon: Icon, fields, values, onChange, period }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-sm text-zinc-300 font-semibold">
      <Icon className="w-4 h-4 text-zinc-500" />
      <span>{title}</span>
      <span className="text-[10px] uppercase tracking-wider text-zinc-600 ml-auto">{period}</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {fields.map((f) => (
        <div key={f.key} className="flex items-center gap-2">
          <Label className="text-[11px] text-zinc-400 flex-1 truncate" title={f.label}>{f.label}</Label>
          <div className="flex items-center gap-1 w-32">
            <Input
              type="number"
              step="0.01"
              value={values[f.key] ?? ''}
              onChange={(e) => onChange(f.key, e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm text-right"
              placeholder={f.placeholder}
              data-testid={`company-${f.key}`}
            />
            <span className="text-[10px] text-zinc-500 w-3">{f.suffix || (period === '/an' ? '€' : '€')}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const CompanySettingsCard = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
      setSettings(res.data);
    } catch {
      toast.error('Impossible de charger les frais de structure');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateField = (key, value) => {
    const v = value === '' ? '' : Number(value);
    setSettings((prev) => ({ ...prev, [key]: v }));
  };

  // Recompute live (preview) before save
  const fraisFixesMensuels = useMemo(() => {
    const annualKeys = ANNUEL_FIELDS.map((f) => f.key);
    const monthlyKeys = [...MENSUEL_FIELDS, ...VEHICULE_FIELDS].map((f) => f.key)
      .concat(['prevoyance_mensuelle', 'mutuelle_mensuelle']);
    const an = annualKeys.reduce((s, k) => s + (Number(settings[k]) || 0), 0);
    const me = monthlyKeys.reduce((s, k) => s + (Number(settings[k]) || 0), 0);
    const rem = (Number(settings.remuneration_brute_mensuelle) || 0)
      * (1 + (Number(settings.charges_remuneration_pct) || 0) / 100);
    return Math.round((an / 12 + me + rem) * 100) / 100;
  }, [settings]);

  const fraisFixesAnnuels = Math.round(fraisFixesMensuels * 12 * 100) / 100;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      [...ANNUEL_FIELDS, ...MENSUEL_FIELDS, ...VEHICULE_FIELDS, ...REMUNERATION_FIELDS].forEach((f) => {
        payload[f.key] = Number(settings[f.key]) || 0;
      });
      payload.nb_projets_paralleles = Math.max(1, Number(settings.nb_projets_paralleles) || 1);
      const res = await axios.put(`${API}/company-settings`, payload, { withCredentials: true });
      setSettings(res.data);
      toast.success(`Enregistré · ${fmt(res.data._frais_fixes_mensuels)} €/mois imputables`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
        <CardContent className="p-8 text-center text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="company-settings-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Building className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
          Frais de structure société
        </CardTitle>
        <p className="text-xs text-zinc-500 mt-1">
          Renseignez ici tous les frais fixes de votre activité. Ils seront automatiquement
          imputés au prorata de la durée de portage dans chaque étude de rentabilité.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Vue d'ensemble */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-lg bg-gradient-to-br from-emerald-950/30 to-zinc-950 border border-emerald-500/20">
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Total mensuel</div>
            <div className="text-lg font-bold text-emerald-400 font-mono" data-testid="company-total-mensuel">{fmt(fraisFixesMensuels)} €</div>
          </div>
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Total annuel</div>
            <div className="text-lg font-bold text-emerald-400 font-mono" data-testid="company-total-annuel">{fmt(fraisFixesAnnuels)} €</div>
          </div>
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Imputation par projet</div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-xs">÷</span>
              <Input
                type="number"
                min="1"
                step="1"
                value={settings.nb_projets_paralleles ?? 1}
                onChange={(e) => updateField('nb_projets_paralleles', e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white h-8 w-20 text-center"
                data-testid="company-nb-paralleles"
              />
              <span className="text-zinc-500 text-xs">projet(s) en parallèle</span>
            </div>
          </div>
        </div>

        <FieldGroup title="Assurances & cotisations obligatoires" icon={Wallet} fields={ANNUEL_FIELDS.slice(0, 6)} values={settings} onChange={updateField} period="/an" />
        <FieldGroup title="Comptable, juridique, divers" icon={Briefcase} fields={ANNUEL_FIELDS.slice(6)} values={settings} onChange={updateField} period="/an" />
        <FieldGroup title="Frais récurrents bureau" icon={Home} fields={MENSUEL_FIELDS} values={settings} onChange={updateField} period="/mois" />
        <FieldGroup title="Véhicule pro" icon={Car} fields={VEHICULE_FIELDS} values={settings} onChange={updateField} period="/mois" />
        <FieldGroup title="Rémunération du gérant" icon={Banknote} fields={REMUNERATION_FIELDS} values={settings} onChange={updateField} period="/mois" />

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-company-settings">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer les frais de structure
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanySettingsCard;
